import * as ReactRedux from 'react-redux';
import * as PopupGroupActions from 'PopupGroup/PopupGroupActions';
import React from 'react';
import PropTypes from 'prop-types';
import PopupGroup from 'PopupGroup';
import CustomPropTypes from 'CustomPropTypes';
import { bindActionCreators } from 'redux';
import { createTextBox, createGroupPopup, clearActivePopup, createSignature } from 'actions';
import { PopupManager, SelfServeBuilderPopupTypes } from 'Popups';
import * as SelfServeBuilderActions from './SelfServeBuilderActions';

const DEFAULT_TOP_PADDING = 18;
const CURSOR_HEIGHT_OFFSET = 30;
const { CHECKBOX, MULTIPLE_CHOICE, TEXTBOX, SIGNATURE } = SelfServeBuilderPopupTypes;


class SelfServeBuilderCanvasOverlay extends React.PureComponent {
  constructor(props) {
    super(props);

    this.addPaddingTop = this.addPaddingTop.bind(this);
    this.clearCanvasOverlay = this.clearCanvasOverlay.bind(this);
    this.createGroupPopup = this.createGroupPopup.bind(this);
    this.createPopup = this.createPopup.bind(this);
    this.getCursorClassName = this.getCursorClassName.bind(this);
    this.isAbleToCreatePopup = this.isAbleToCreatePopup.bind(this);
    this.isGroupDraggableAndResizeable = this.isGroupDraggableAndResizeable.bind(this);
    this.isPopupDraggableAndResizeable = this.isPopupDraggableAndResizeable.bind(this);
    this.isGroupToolBoxItem = this.isGroupToolBoxItem.bind(this);
    this.isValidGroupCreation = this.isValidGroupCreation.bind(this);
    this.onClick = this.onClick.bind(this);
    this.setPaddingTop = this.setPaddingTop.bind(this);
    this.getPopupsOnPage = this.getPopupsOnPage.bind(this);
    this.canvasRef = React.createRef();
    this.state = {
      paddingTop: 0,
    };
  }

  componentDidMount() {
    const { activePopup, popupsOnPage, pageIndex } = this.props;
    const popupIdsOnPage = Object.keys(popupsOnPage);
    if (activePopup && popupIdsOnPage.includes(activePopup)) {
      this.props.setCurrentPage(pageIndex);
    }
  }

  onClick(e) {
    if (!e.target.contains(this.canvasRef.current)) {
      return;
    }
    if (this.isAbleToCreatePopup()) {
      this.createPopup(e);
    } else {
      this.props.setPopupCreationError();
    }
  }

  // cursor at most can be 128x128, but should limit to 32x32
  // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_User_Interface/Using_URL_values_for_the_cursor_property#Limitations
  getCursorClassName() {
    const { popupCreationEnabled, isEditingGroup, isPreviewMode } = this.props;
    if ((!popupCreationEnabled && !isEditingGroup) || isPreviewMode) {
      return 'auto';
    }
    switch (this.props.activeToolboxItem) {
      case CHECKBOX:
        return 'with-checkbox-cursor';
      case MULTIPLE_CHOICE:
        return 'with-mult-choice-cursor';
      case SIGNATURE:
        return 'with-signature-cursor';
      case TEXTBOX: {
        if (this.props.isEditingGroup) {
          return 'auto';
        }
        return 'with-textbox-cursor';
      }
      default:
        return '';
    }
  }

  setPaddingTop(padding) {
    const paddingTop = isNaN(padding) ? 0 : padding;
    this.setState({ paddingTop });
  }

  getPopupsOnPage() {
    const popupIdsOnPage = Object.keys(this.props.popupsOnPage);
    return (
      <React.Fragment>
        {
          popupIdsOnPage.map(id => (
            <PopupManager
              addPaddingTop={this.addPaddingTop}
              bounds={`.page[data-page-number='${this.props.pageIndex + 1}']`}
              draggable={this.isPopupDraggableAndResizeable(id)}
              getSuperScriptValue={this.props.getSuperScriptValue}
              highlightedFieldId={this.props.highlightedFieldId}
              id={id}
              key={id}
              popupData={this.props.popupsOnPage[id]}
              resizable={this.isPopupDraggableAndResizeable(id)}
              isPreviewMode={this.props.isPreviewMode}
              zoomFactor={this.props.zoomFactor}
            />
          ))
        }
      </React.Fragment>
    );
  }

  getGroupPopupsOnPage() {
    const groupPopupIdsOnPage = Object.keys(this.props.popupGroupsOnPage);
    const { isPreviewMode } = this.props;
    return (
      <React.Fragment>
        {
          groupPopupIdsOnPage.map((id) => {
            const popupGroup = this.props.popupGroupsOnPage[id];
            const popupsInGroup = {};
            popupGroup.fieldIds.forEach((key) => {
              popupsInGroup[key] = this.props.popupsOnPage[key];
            });
            return (
              <PopupGroup
                addPaddingTop={this.addPaddingTop}
                getSuperScriptValue={this.props.getSuperScriptValue}
                groupId={id}
                isActive={id === this.props.activeGroupId && !isPreviewMode}
                isDraggable={this.isGroupDraggableAndResizeable}
                isResizeable={this.isGroupDraggableAndResizeable}
                pageIndex={this.props.pageIndex}
                key={id}
                popupGroup={popupGroup}
                popupsInGroup={popupsInGroup}
                isPreviewMode={isPreviewMode}
                zoomFactor={this.props.zoomFactor}
              />
            );
          })
        }
      </React.Fragment>
    );
  }

  isGroupDraggableAndResizeable(groupId) {
    if (this.props.isEditingGroup) {
      return this.props.activeGroupId === groupId;
    }
    return this.props.popupCreationEnabled;
  }

  isPopupDraggableAndResizeable(id) {
    if (this.props.isEditingGroup) return false;
    return (
      this.props.popupCreationEnabled ||
      (!this.props.popupCreationEnabled && (this.props.activePopup === id))
    );
  }

  createGroupPopup(e) {
    e.persist();
    let groupId = this.props.activeGroupId;
    const type = this.props.activeToolboxItem;
    // If no existing group, create one
    if (!groupId || !this.props.isEditingGroup) {
      this.clearCanvasOverlay();
      // Wait on the group to be created so we can assign
      // the checkbox popup to appropriate group ID
      const groupPopupCreationAction = this.props.createGroup(
        this.props.pageIndex,
        type,
      );
      groupId = groupPopupCreationAction.groupId;
    }
    const groupPopup = this.props.createGroupPopup(
      this.props.pageIndex,
      groupId,
      type,
      e.nativeEvent.offsetX,
      Math.max(e.nativeEvent.offsetY - this.state.paddingTop - CURSOR_HEIGHT_OFFSET, 0),
    );
    this.props.addToGroup(groupId, groupPopup.id);
  }

  clearCanvasOverlay() {
    this.props.clearActivePopup();
    this.props.clearActiveGroupPopup();
  }

  createPopup(e) {
    switch (this.props.activeToolboxItem) {
      case MULTIPLE_CHOICE:
      case CHECKBOX:
        this.createGroupPopup(e);
        this.props.disablePopupCreation();
        break;
      case SIGNATURE:
        this.clearCanvasOverlay();
        this.props.createSignature(
            this.props.pageIndex,
            e.nativeEvent.offsetX,
            Math.max(e.nativeEvent.offsetY - CURSOR_HEIGHT_OFFSET, 0),
          );
        this.props.disablePopupCreation();
        break;
      case TEXTBOX: {
        this.clearCanvasOverlay();
        this.props.createTextBox(
          this.props.pageIndex,
          e.nativeEvent.offsetX,
          Math.max(e.nativeEvent.offsetY - CURSOR_HEIGHT_OFFSET, 0),
        );
        this.props.disablePopupCreation();
        break;
      }
      default:
        break;
    }
  }

  isAbleToCreatePopup() {
    const { isEditingGroup } = this.props;
    if (isEditingGroup) {
      return this.isValidGroupCreation();
    }
    return this.props.popupCreationEnabled;
  }

  isGroupToolBoxItem() {
    const { activeToolboxItem } = this.props;
    return activeToolboxItem === CHECKBOX;
  }

  isValidGroupCreation() {
    const { activeToolboxItem, activePopup, popupsOnPage } = this.props;
    const samePage = activePopup in popupsOnPage;
    if (samePage) {
      const activePopupType = popupsOnPage[activePopup].type;
      const sameType = activePopupType === activeToolboxItem;
      return sameType;
    }
    return false;
  }

  addPaddingTop(id, padding) {
    if (this.props.pageIndex === 0) {
      let paddingTop = 0;
      const diffFromTop = this.props.popupsOnPage[id].y - padding;
      if (diffFromTop < 0) {
        paddingTop = Math.abs(diffFromTop) + DEFAULT_TOP_PADDING;
      }
      this.props.addPaddingTop(paddingTop);
      this.setPaddingTop(paddingTop);
    }
  }

  render() {
    const { width, height } = this.props;
    const cursorClassName = this.getCursorClassName();
    return (
      <div
        className={`self-serve-builder-canvas ${cursorClassName}`}
        ref={this.canvasRef}
        onClick={this.onClick}
        style={{ height, width, paddingTop: this.state.paddingTop }}
      >
        {this.getPopupsOnPage()}
        {this.getGroupPopupsOnPage()}
      </div>
    );
  }
}


SelfServeBuilderCanvasOverlay.defaultProps = {
  activeGroupId: '',
  activePopup: '',
  activeToolboxItem: '',
  zoomFactor: 1,
};

SelfServeBuilderCanvasOverlay.propTypes = {
  activeGroupId: PropTypes.string,
  activePopup: PropTypes.string,
  activeToolboxItem: PropTypes.string,
  addToGroup: PropTypes.func.isRequired,
  addPaddingTop: PropTypes.func.isRequired,
  clearActiveGroupPopup: PropTypes.func.isRequired,
  clearActivePopup: PropTypes.func.isRequired,
  createGroupPopup: PropTypes.func.isRequired,
  createGroup: PropTypes.func.isRequired,
  createSignature: PropTypes.func.isRequired,
  createTextBox: PropTypes.func.isRequired,
  disablePopupCreation: PropTypes.func.isRequired,
  getSuperScriptValue: PropTypes.func.isRequired,
  height: PropTypes.number.isRequired,
  highlightedFieldId: PropTypes.string.isRequired,
  isEditingGroup: PropTypes.bool.isRequired,
  isPreviewMode: PropTypes.bool.isRequired,
  pageIndex: PropTypes.number.isRequired,
  popupGroupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
  popupsOnPage: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      id: PropTypes.string,
      pageIdx: PropTypes.number,
    }),
  ).isRequired,
  popupCreationEnabled: PropTypes.bool.isRequired,
  setPopupCreationError: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  width: PropTypes.number.isRequired,
  zoomFactor: PropTypes.number,
};

const mapStateToProps = ({
  SelfServeBuilderReducer,
  SelfServeBuilderToolboxReducer,
  popupHandler,
  PopupGroupReducer,
}) => ({
  activeGroupId: PopupGroupReducer.activeGroupId,
  activePopup: popupHandler.activePopup,
  activeToolboxItem: SelfServeBuilderToolboxReducer.activeToolboxItem,
  highlightedFieldId: SelfServeBuilderReducer.highlightedFieldId,
  isEditingGroup: PopupGroupReducer.isEditingGroup,
  popupCreationEnabled: SelfServeBuilderReducer.popupCreationEnabled,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    addToGroup: PopupGroupActions.addToGroup,
    clearActiveGroupPopup: PopupGroupActions.clearActiveGroupPopup,
    createGroupPopup,
    createGroup: PopupGroupActions.createGroup,
    createSignature,
    createTextBox,
    clearActivePopup,
    disablePopupCreation: SelfServeBuilderActions.disablePopupCreation,
    setCurrentPage: SelfServeBuilderActions.setCurrentPage,
    setPopupCreationError: SelfServeBuilderActions.setPopupCreationError,
  }, dispatch)
);

const SelfServeBuilderCanvasOverlayWithRedux = ReactRedux.connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelfServeBuilderCanvasOverlay);

export default SelfServeBuilderCanvasOverlayWithRedux;



// WEBPACK FOOTER //
// ./src-react/SelfServeBuilder/SelfServeBuilderCanvasOverlay.jsx